import ky from 'ky'
import shortid from 'shortid'
import { Session } from '@chilipiper/api-type-def'
import { testing } from '@chilipiper/config'
import * as __session from './session'
import { api, profiles, reportingApi, profilesUpload as profilesUploadEndpoint } from './endpoint'
import { store } from './store'
import { captureException } from './sentry'

const apiRoot = api('/api/v1/')

const TIMEOUT_IN_SEC = 60000
const RETRY_LIMIT = 5

const isSessionCall = (url: string) => url.endsWith('v1/session')

export type CreateInstance = (apiRoot: string, contentType?: string) => typeof ky

interface Error {
  error: string
  type: string
}

type SessionResponse = Session | Error

export const createInstance: CreateInstance = (apiRoot: string, contentType?: string) => {
  const instance = ky.create({
    prefixUrl: process.env.NODE_ENV === 'test' ? apiRoot.replace('team', 'io') : apiRoot,
    mode: 'cors',
    timeout: TIMEOUT_IN_SEC,
    retry: {
      limit: RETRY_LIMIT,
      methods: ['get', 'put', 'post', 'delete', 'options'],
    },
    credentials: 'include',
    headers: {
      Accept: '*/*',
      'x-request-id': shortid.generate(),
      ...(contentType ? { 'content-type': contentType } : {}),
      ...(__session.getSessionId() ? { 'special-session-id': __session.getSessionId() || '' } : {}),
    },
    hooks: {
      beforeRequest: [
        request => {
          const token = getToken()
          if (token) {
            request.headers.set('Authorization', `Bearer ${token}`)
          }

          if (__session.getSessionId()) {
            request.headers.set('special-session-id', __session.getSessionId())
          }
        },
      ],
      afterResponse: [
        async (request, _options, response) => {
          if (response.status === 200 || isSessionCall(request.url)) return response
          if ([401, 403].includes(response.status)) {
            try {
              clearToken()
              await session()
              return ky(request)
            } catch (err) {
              console.error('Session error', err)
              return response
            }
          }
          return response
        },
      ],
    },
  })

  return instance
}

function clearToken() {
  return store.remove('token')
}

export function getToken() {
  return store.get('token')
}

export function setToken(token: string) {
  store.set('token', token)
}

export async function session() {
  try {
    const hasToken = getToken()
    const hasUser = __session.getUser()?.id
    if (hasToken && hasUser) {
      return Promise.resolve(__session.getSession())
    }
    const body = await get(
      'session',
      process.env.NODE_ENV === 'test'
        ? {
            headers: {
              'special-session-id': testing.sessionId,
            },
          }
        : {}
    ).json<SessionResponse>()

    if ((body as Error).error) {
      throw body
    }
    __session.create(body as Session)
    setToken((body as Session).token as string)
    return body as Session
  } catch (err) {
    throw err
  }
}

export function logout() {
  store.remove('token')
  store.remove('sessionId')

  window.location.href = api('/logout')
}

export function defaultErrorCallback(error: Error) {
  captureException(error)
}

const rootInstance = createInstance('')
const instance = createInstance(apiRoot, 'application/json')
const profilesInstance = createInstance(profiles())
const reportingInstance = createInstance(reportingApi('/api/v1'))

export const { post, put, get, patch } = instance

export const del = instance.delete

export const { get: profileGet, post: profilePost, patch: profilePatch } = profilesInstance

export const { get: reportingGet, post: reportingPost } = reportingInstance

export const { post: profileUpload } = createInstance(profilesUploadEndpoint())

export const { get: rootGet } = rootInstance
